import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnDestroy, AfterViewInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { Chart, ChartConfiguration, ChartData, ChartDataset } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
Chart.register(ChartDataLabels);

@Component({
  selector: 'app-weekly-chart-dialog',
  templateUrl: './weekly-chart-dialog.component.html',
  styleUrls: ['./weekly-chart-dialog.component.scss']
})
export class WeeklyChartDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() visible: boolean = false;
  @Input() chartType: 'revenue' | 'quantity' = 'revenue';
  @Input() chartData: ChartData<'line'> = { labels: [], datasets: [] };
  @Input() selectedYear: number = new Date().getFullYear();
  @Input() customerCount: number = 0;
  @Input() monthCount: number = 0;
  @Input() totalValue: number = 0;
  @Input() formatCurrency: (value: number) => string = (value) => value.toString();
  @Input() formatNumber: (value: number) => string = (value) => value.toString();

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() exportChart = new EventEmitter<'revenue' | 'quantity'>();
  @Output() closeDialog = new EventEmitter<void>();

  // Use static: true to ensure ViewChild is available immediately
  @ViewChild('chartCanvas', { static: true }) chartCanvas!: ElementRef<HTMLCanvasElement>;

  chart: Chart | null = null;
  isChartLoading: boolean = false;
  showCanvas: boolean = false;
  isMaximized: boolean = false;
  private chartCreationTimer: any = null;

  constructor(
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    // Initialize component
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  ngOnDestroy() {
    this.destroyChart();
    this.clearChartTimer();
  }

  onShow() {
    console.log('Dialog showing...');
    console.log('Chart data received:', this.chartData);

    this.isChartLoading = true;
    this.showCanvas = false;
    this.clearChartTimer();

    // Force canvas to be visible first, then create chart
    this.chartCreationTimer = setTimeout(() => {
      this.showCanvas = true;
      this.cdr.detectChanges();

      // Give canvas time to render with dimensions
      setTimeout(() => {
        this.createChart();
      }, 100);
    }, 800);
  }

  onHide() {
    this.destroyChart();
    this.clearChartTimer();
    this.isChartLoading = false;
    this.showCanvas = false;
    this.isMaximized = false;
    this.visibleChange.emit(false);
    this.closeDialog.emit();
  }

  onMaximize() {
    console.log('Dialog maximized');
    this.isMaximized = true;
    this.cdr.detectChanges();

    // Resize chart after maximizing
    setTimeout(() => {
      if (this.chart) {
        this.chart.resize();
      }
    }, 300);
  }

  onUnmaximize() {
    console.log('Dialog unmaximized');
    this.isMaximized = false;
    this.cdr.detectChanges();

    // Resize chart after unmaximizing
    setTimeout(() => {
      if (this.chart) {
        this.chart.resize();
      }
    }, 300);
  }

  toggleFullscreen() {
    // This method can be used if you want to programmatically toggle fullscreen
    // The actual maximize/minimize is handled by PrimeNG dialog's maximize button
    console.log('Toggle fullscreen clicked');
  }

  private clearChartTimer() {
    if (this.chartCreationTimer) {
      clearTimeout(this.chartCreationTimer);
      this.chartCreationTimer = null;
    }
  }

  createChart(): void {
    console.log('Creating chart with real data...');

    // Destroy existing chart first
    if (this.chart) {
      this.ngZone.runOutsideAngular(() => {
        this.chart?.destroy();
      });
      this.chart = null;
    }

    if (!this.chartCanvas || !this.chartCanvas.nativeElement) {
      console.error('Canvas not available');
      this.isChartLoading = false;
      return;
    }

    const canvas = this.chartCanvas.nativeElement;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('Could not get canvas context');
      this.isChartLoading = false;
      return;
    }

    // Validate chart data
    if (!this.chartData || !this.chartData.labels || !this.chartData.datasets) {
      console.error('Invalid chart data:', this.chartData);
      this.isChartLoading = false;
      return;
    }

    // Calculate total dataset
    const totalData = this.calculateTotalData();

    // Add total dataset to chart data
    const chartDataWithTotal = {
      ...this.chartData,
      datasets: [
        ...this.chartData.datasets,
        {
          label: 'Total',
          data: totalData,
          borderColor: '#ff6b6b',
          backgroundColor: 'rgba(255, 107, 107, 0.1)',
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          tension: 0.4,
          fill: false,
          borderDash: [5, 5], // Dashed line for total
          pointBackgroundColor: '#ff6b6b',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2
        }
      ]
    };

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: chartDataWithTotal,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: 1000,
          easing: 'easeInOutQuart'
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        elements: {
          point: {
            radius: this.isMaximized ? 6 : 4,
            hoverRadius: this.isMaximized ? 9 : 7
          },
          line: {
            tension: 0.4,
            borderWidth: this.isMaximized ? 3 : 2
          }
        },
        plugins: {
          // Enable datalabels ONLY for Total dataset
          datalabels: {
            align: 'top',
            anchor: 'end',
            backgroundColor: 'rgba(255, 107, 107, 0.9)', // Red background for total labels
            borderColor: '#fff',
            borderRadius: 4,
            borderWidth: 1,
            color: '#fff',
            font: {
              size: this.isMaximized ? 12 : 10,
              weight: 'bold'
            },
            padding: {
              top: 4,
              bottom: 4,
              left: 6,
              right: 6
            },
            formatter: (value, context) => {
              // Only format for Total dataset, others will be hidden
              if (context.dataset.label === 'Total') {
                // Custom formatting: remove currency symbol and decimals, show full number
                const roundedValue = Math.round(value); // Round to nearest integer
                return `${roundedValue.toLocaleString()}`; // Add thousand separators without currency
              }
              // Return null/undefined to hide labels for other datasets
              return null;
            },
            // Show ONLY for Total dataset - hide all others
            display: (context) => {
              // Only show datalabels for the Total dataset
              return context.dataset.label === 'Total';
            }
          },
          title: {
            display: true,
            text: `Cumulative Order ${this.chartType === 'revenue' ? 'Values' : 'Numbers'} Analysis - ${this.selectedYear}`,
            font: {
              size: this.isMaximized ? 24 : 18,
              weight: 'bold'
            },
            color: '#1e293b',
            padding: this.isMaximized ? 30 : 20
          },
          legend: {
            display: true,
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: this.isMaximized ? 20 : 15,
              font: {
                size: this.isMaximized ? 14 : 12,
                weight: '600'
              },
              generateLabels: (chart) => {
                const original = Chart.defaults.plugins.legend.labels.generateLabels;
                const labels = original.call(this, chart);

                // Highlight total dataset in legend
                labels.forEach((label, index) => {
                  if (label.text === 'Total') {
                    label.strokeStyle = '#ff6b6b';
                  }
                });

                return labels;
              }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            titleColor: '#fff',
            bodyColor: '#fff',
            borderColor: this.chartType === 'revenue' ? '#3b82f6' : '#8b5cf6',
            borderWidth: 1,
            cornerRadius: 8,
            displayColors: true,
            titleFont: {
              size: this.isMaximized ? 14 : 12
            },
            bodyFont: {
              size: this.isMaximized ? 13 : 11
            },
            callbacks: {
              label: (context) => {
                // Custom tooltip formatting - remove currency and show rounded values
                const roundedValue = Math.round(context.parsed.y);
                const formattedValue = roundedValue.toLocaleString(); // Add commas without currency
                return `${context.dataset.label}: ${formattedValue}`;
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Months',
              font: {
                size: this.isMaximized ? 16 : 14,
                weight: 'bold'
              },
              color: '#374151'
            },
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.1)'
            },
            ticks: {
              font: {
                size: this.isMaximized ? 14 : 12,
                weight: '500'
              },
              color: '#6b7280'
            }
          },
          y: {
            display: true,
            title: {
              display: true,
              text: `Accumulative ${this.chartType === 'revenue' ? 'Revenue ($K)' : 'Quantity'}`, // Added ($K) for revenue
              font: {
                size: this.isMaximized ? 16 : 14,
                weight: 'bold'
              },
              color: '#374151'
            },
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.1)'
            },
            ticks: {
              font: {
                size: this.isMaximized ? 14 : 12,
                weight: '500'
              },
              color: '#6b7280',
              callback: (value) => {
                // Y-axis formatting: remove currency and K/M suffixes, show rounded values
                const roundedValue = Math.round(value as number);
                return roundedValue.toLocaleString(); // Shows like 1,000 instead of 1K
              }
            }
          }
        }

      }
    };

    try {
      // Run Chart.js creation outside Angular's zone
      this.ngZone.runOutsideAngular(() => {
        this.chart = new Chart(ctx, config);
      });

      console.log('Chart created successfully with total data and datalabels!');
      this.isChartLoading = false;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error creating chart:', error);
      this.isChartLoading = false;
      this.cdr.detectChanges();
    }
  }

  private calculateTotalData(): number[] {
    if (!this.chartData?.datasets || this.chartData.datasets.length === 0) {
      return [];
    }

    const dataLength = this.chartData.datasets[0]?.data?.length || 0;
    const totalData: number[] = [];

    // Calculate sum for each month across all customer datasets
    for (let i = 0; i < dataLength; i++) {
      let monthTotal = 0;

      this.chartData.datasets.forEach(dataset => {
        const value = dataset.data[i];
        if (typeof value === 'number') {
          monthTotal += value;
        }
      });

      totalData.push(monthTotal);
    }

    return totalData;
  }

  destroyChart(): void {
    if (this.chart) {
      this.ngZone.runOutsideAngular(() => {
        this.chart?.destroy();
      });
      this.chart = null;
    }
  }

  onExportChart(): void {
    this.exportChart.emit(this.chartType);
  }

  onCloseChart(): void {
    this.visible = false;
    this.onHide();
  }

  formatCurrencyShort(value: number): string {
    if (value >= 1000000) {
      return `$ ${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$ ${(value / 1000).toFixed(0)}K`;
    }
    return this.formatCurrency(value);
  }

  get hasValidData(): boolean {
    return !!(this.chartData &&
      this.chartData.labels &&
      this.chartData.labels.length > 0 &&
      this.chartData.datasets &&
      this.chartData.datasets.length > 0);
  }

  get dialogHeader(): string {
    return `Accumulative ${this.chartType === 'revenue' ? 'Revenue' : 'Quantity'} Chart`;
  }

  get chartIcon(): string {
    return this.chartType === 'revenue' ? 'pi-chart-line' : 'pi-chart-bar';
  }

  get totalDisplayValue(): string {
    return this.chartType === 'revenue'
      ? this.formatCurrency(this.totalValue)
      : this.formatNumber(this.totalValue);
  }

  get totalLabel(): string {
    return this.chartType === 'revenue' ? 'Total Revenue' : 'Total Orders';
  }

  get canvasWidth(): number {
    return this.isMaximized ? 1200 : 800;
  }

  get canvasHeight(): number {
    return this.isMaximized ? 600 : 400;
  }
}
